<script setup lang="ts">
import { Bot, Cable, ChevronRight, MessageSquare, MessageSquareOff, Plug } from 'lucide-vue-next'

const { t } = useI18n()

interface SettingsEntry {
  page: string
  icon: typeof Plug
  title: string
  description: string
}

interface SettingsSection {
  key: string
  title: string
  icon: typeof Plug
  iconClass: string
  tileClass: string
  entries: SettingsEntry[]
}

const sections = computed<SettingsSection[]>(() => [
  {
    key: 'connection',
    title: t('pages.eReputation.settings.sections.connection'),
    icon: Cable,
    iconClass: 'text-blue-600',
    tileClass: 'bg-blue-50 text-blue-700',
    entries: [
      {
        page: 'settingsConnection',
        icon: Plug,
        title: t('pages.eReputation.settings.platforms.title'),
        description: t('pages.eReputation.settings.platforms.description')
      }
    ]
  },
  {
    key: 'autoReply',
    title: t('pages.eReputation.settings.sections.autoReply'),
    icon: Bot,
    iconClass: 'text-primary',
    tileClass: 'bg-primary/10 text-primary',
    entries: [
      {
        page: 'reviewAutoReplyWithoutComments',
        icon: MessageSquareOff,
        title: t('pages.eReputation.autoReply.withoutComments.title'),
        description: t('pages.eReputation.autoReply.withoutComments.description')
      },
      {
        page: 'reviewAutoReplyWithComments',
        icon: MessageSquare,
        title: t('pages.eReputation.autoReply.withComments.title'),
        description: t('pages.eReputation.autoReply.withComments.description')
      }
    ]
  }
])
</script>

<template>
  <div class="flex min-h-screen items-center justify-center px-4 py-8">
    <div class="w-full max-w-4xl">
      <h1 class="text-3xl font-medium tracking-tight text-foreground">{{ t('pages.eReputation.settings.title') }}</h1>

      <section v-for="section in sections" :key="section.key" class="mt-7">
        <h2
          class="mb-2.5 flex items-center gap-2 px-0.5 text-xs font-medium uppercase tracking-wide text-muted-foreground"
        >
          <component :is="section.icon" :class="['size-4', section.iconClass]" />
          {{ section.title }}
        </h2>
        <div class="divide-y divide-border overflow-hidden rounded-2xl border border-border bg-card">
          <RouterLink
            v-for="entry in section.entries"
            :key="entry.page"
            :to="{ name: 'EReputation', params: { page: entry.page } }"
            class="flex items-center gap-4 p-4 transition-colors hover:bg-muted/50"
          >
            <span :class="['flex size-11 shrink-0 items-center justify-center rounded-xl', section.tileClass]">
              <component :is="entry.icon" class="size-5" />
            </span>
            <div class="min-w-0 flex-1">
              <p class="font-medium text-foreground">{{ entry.title }}</p>
              <p class="mt-0.5 text-sm text-muted-foreground">{{ entry.description }}</p>
            </div>
            <ChevronRight class="size-5 shrink-0 text-muted-foreground" />
          </RouterLink>
        </div>
      </section>
    </div>
  </div>
</template>
